import {
  Accordion, AccordionDetails, AccordionSummary, Button, Card, CardContent, Link, Stack, TextField, Typography,
} from '@mui/material';
import { ContentCopyOutlined, EmailOutlined, ExpandMore, OpenInNewOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import moment from 'moment';
import { FunctionComponent, useContext, useState } from 'react';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import CloudEmissionsService from '../shared-services/cloud-emissions/cloud-emissions.service';
import { ICloudConnectionStatus } from '../shared-services/cloud-emissions/cloud-emissions.model';
import { SetupInstructionsDialog } from '../shared-components/components/setup-instructions-dialog';
import { AWS_INSTRUCTIONS_NOTE_MAX } from '../shared-services/cloud-emissions/cloud-emissions.model';
import { CodeBlock, CopyRow } from './cloud-connect-blocks';

const cardSx = { borderColor: 'lightGrey.main', borderRadius: '10px', boxShadow: 'none' };

/**
 * Connecting AWS: the customer creates a cross-account IAM role that trusts one principal of
 * ours, requires their external id, and may only read carbon estimates. No AWS secret is ever
 * entered here.
 *
 * The person who can create that role is usually not our user but their organisation's AWS admin,
 * so the panel is built around handing the job over: email the instructions (or copy them into a
 * ticket), let the admin create the role in one click from CloudFormation or by hand, then paste
 * back the ARN they send. Connect tests the role before saving it, so a half-made role never
 * looks connected.
 */
export const AwsConnectPanel: FunctionComponent<{
  status: ICloudConnectionStatus,
  isAdmin: boolean,
  onUpdated: (status: ICloudConnectionStatus) => void,
}> = ({ status, isAdmin, onUpdated }) => {
  const cloudEmissionsService = new CloudEmissionsService();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const [roleArn, setRoleArn] = useState(status.connection?.roleArn ?? '');
  const [isStarting, setIsStarting] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [prefill, setPrefill] = useState<{ email: string, name: string }>({ email: '', name: '' });

  const externalId = status.aws?.externalId ?? null;
  const principalArn = status.aws?.principalArn ?? null;
  const launchUrl = status.aws?.launchUrl ?? null;
  const instructionsText = status.aws?.instructionsText ?? null;
  const lastSent = status.aws?.lastInstructionsSent ?? null;

  const errorSnackbar = (err: any, fallback: string) => userNotificationsContext.openSnackbar({
    open: true,
    message: err?.response?.data?.message || fallback,
    backgroundColor: 'text.primary',
  });

  const copy = (text: string, successMessage: string) => {
    navigator.clipboard.writeText(text)
      .then(() => userNotificationsContext.openSnackbar({ open: true, message: successMessage, backgroundColor: 'text.primary' }))
      .catch(() => userNotificationsContext.openSnackbar({ open: true, message: 'Could not copy; select the text by hand', backgroundColor: 'text.primary' }));
  };

  const startSetup = () => {
    setIsStarting(true);
    cloudEmissionsService.setupAws()
      .then(onUpdated)
      .catch(err => errorSnackbar(err, 'The AWS setup could not be started. Please try again.'))
      .finally(() => setIsStarting(false));
  };

  const openDialog = (resend: boolean) => {
    setPrefill(resend && lastSent ? { email: lastSent.email, name: lastSent.name ?? '' } : { email: '', name: '' });
    setDialogOpen(true);
  };

  const connect = () => {
    setIsConnecting(true);
    setConnectError(null);
    cloudEmissionsService.connectAws(roleArn)
      .then(updated => {
        const count = updated.connectResult?.accountCount;
        userNotificationsContext.openSnackbar({
          open: true,
          message: count === 0 || count === undefined
            ? 'AWS connected'
            : `AWS connected. The role can see carbon figures for ${count} ${count === 1 ? 'account' : 'accounts'}.`,
          backgroundColor: 'primary.dark',
        });
        onUpdated(updated);
      })
      .catch(err => setConnectError(err?.response?.data?.message || 'The role could not be saved.'))
      .finally(() => setIsConnecting(false));
  };

  if (!isAdmin) {
    return (
      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
        Ask an admin in your organisation to connect AWS.
      </Typography>
    );
  }

  if (!externalId) {
    return (
      <Card variant='outlined' sx={cardSx}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' alignItems={{ xs: 'flex-start', md: 'center' }} gap={2}>
            <Stack spacing={1}>
              <Typography variant='h6' sx={{ color: 'text.primary' }}>Set up AWS access</Typography>
              <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '680px' }}>
                You will create a read-only IAM role in your AWS account. We never ask for AWS keys or
                passwords: the role can read carbon estimates and nothing else, and you can delete it at any time.
              </Typography>
            </Stack>
            <LoadingButton variant='contained' loading={isStarting} onClick={startSetup}>
              <Typography>Start setup</Typography>
            </LoadingButton>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const permissionsPolicy = JSON.stringify(status.aws?.permissionsPolicy ?? {}, null, 2);
  const trustPolicy = status.aws?.trustPolicy ? JSON.stringify(status.aws.trustPolicy, null, 2) : '';

  return (
    <Card variant='outlined' sx={cardSx}>
      <CardContent>
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Typography variant='h6' sx={{ color: 'text.primary' }}>Connect AWS</Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '680px' }}>
              Someone who can create IAM roles in your AWS management account needs to create one read-only
              role. If that isn&apos;t you, email them the instructions.
            </Typography>
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant='subtitle1' sx={{ color: 'text.primary' }}>Step 1: create the role</Typography>
            {/* Flex gap, not spacing: wrapped buttons start aligned with a gap above them. */}
            <Stack direction='row' flexWrap='wrap' useFlexGap gap={2} alignItems='center'>
              <Button variant='contained' startIcon={<EmailOutlined fontSize='small' />} onClick={() => openDialog(false)}>
                <Typography sx={{ pl: 0.5 }}>Email instructions</Typography>
              </Button>
              {launchUrl && (
                <Button
                  variant='outlined'
                  startIcon={<OpenInNewOutlined fontSize='small' />}
                  href={launchUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <Typography sx={{ pl: 0.5 }}>Create the role in AWS</Typography>
                </Button>
              )}
              <Button
                variant='text'
                startIcon={<ContentCopyOutlined fontSize='small' />}
                disabled={!instructionsText}
                onClick={() => instructionsText && copy(instructionsText, 'Instructions copied. Paste them into your ticket or message.')}
              >
                <Typography sx={{ pl: 0.5 }}>Copy instructions</Typography>
              </Button>
            </Stack>
            {lastSent && (
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                Instructions sent to {lastSent.email} on {moment(lastSent.sentAt).format('D MMM YYYY')}
                {lastSent.sentByName ? ` by ${lastSent.sentByName}` : ''}.{' '}
                <Link component='button' type='button' onClick={() => openDialog(true)} sx={{ verticalAlign: 'baseline' }}>
                  Resend
                </Link>
              </Typography>
            )}
          </Stack>

          <Accordion
            defaultExpanded={!launchUrl}
            disableGutters
            sx={{ boxShadow: 0, border: '1px solid', borderColor: 'lightGrey.main', borderRadius: '10px', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2 }}>
              <Typography variant='subtitle1' sx={{ color: 'text.primary' }}>Set it up by hand instead</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pb: 2 }}>
              <Stack spacing={3}>
                <CopyRow label='Trusted principal' value={principalArn ?? 'Not available'} onCopy={principalArn ? () => copy(principalArn, 'Principal copied to your clipboard') : undefined} />
                <CopyRow label='External id' value={externalId} onCopy={() => copy(externalId, 'External id copied to your clipboard')} />
                <CodeBlock title='1. Trust policy (Trust relationships tab)' text={trustPolicy} onCopy={() => copy(trustPolicy, 'Trust policy copied to your clipboard')} />
                <CodeBlock title='2. Permissions policy (inline policy on the role)' text={permissionsPolicy} onCopy={() => copy(permissionsPolicy, 'Permissions policy copied to your clipboard')} />
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Stack spacing={1}>
            <Typography variant='subtitle1' sx={{ color: 'text.primary' }}>Step 2: paste the role ARN</Typography>
            <TextField
              size='small'
              fullWidth
              multiline
              minRows={1}
              maxRows={4}
              placeholder='arn:aws:iam::123456789012:role/carbon-emissions-read-only'
              value={roleArn}
              onChange={e => { setRoleArn(e.target.value); setConnectError(null); }}
              inputProps={{ spellCheck: false, 'aria-label': 'Role ARN' }}
              helperText="Your AWS admin will send you this. In CloudFormation it is on the stack's Outputs tab, labelled RoleArn."
            />
            {connectError && (
              <Typography variant='body2' sx={{ color: 'error.main' }}>{connectError}</Typography>
            )}
            <Stack direction='row' justifyContent='flex-end'>
              <LoadingButton variant='contained' loading={isConnecting} disabled={!roleArn.trim()} onClick={connect}>
                <Typography>Connect</Typography>
              </LoadingButton>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>

      <SetupInstructionsDialog<ICloudConnectionStatus>
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSent={onUpdated}
        send={recipient => cloudEmissionsService.sendAwsInstructions(recipient)}
        adminLabel='AWS admin'
        description={'They will receive what the role can and cannot do (it can only read AWS carbon estimates), a '
          + 'one-click setup link, step-by-step manual instructions with the policies attached, and a request '
          + 'to send the role ARN back to you. Their reply comes to your email address.'}
        noteMax={AWS_INSTRUCTIONS_NOTE_MAX}
        initialEmail={prefill.email}
        initialName={prefill.name}
      />
    </Card>
  );
};
